import { DATE_FORMATS } from '../../utils/Application_Constants';
import { currencyFormatter } from '../../utils/helpers';
import { formatUtcDate } from '../../utils/formatDate';
import { getProcessedTimesheetDetails } from '../Services/ProcessedTimesheetService';

const FLOATING_POINT_OFFSET = 0.00001;

export const PTS_VALIDATION_ERROR_MESSAGE =
    'Unable to validate PTS detail calculations. Please try again or refresh the page.';

export const MILEAGE_BILLABLE_VALIDATION_MESSAGE =
    'Mileage-Billable requires matching bill and pay values, and the Bill Amount cannot be $0 ' +
    'or blank. To pay mileage without billing the client, select a payable-only mileage rate instead.';

export const getLocationsFromDetails = (details) =>
    details?.timeEntries?.entriesByLocation?.map(({ locationName, locationId }) => ({
        locationName,
        locationId,
    })) ?? [];

export const resolveEntriesForValidation = async (
    processedTimesheetId,
    entries = [],
    locationList = []
) => {
    if (entries?.length) {
        return { entries, locations: locationList };
    }

    const details = await getProcessedTimesheetDetails({ processedTimesheetId });
    return {
        entries: details?.processedTimesheetEntries || [],
        locations: getLocationsFromDetails(details),
    };
};

export const PTS_STATUSES_REQUIRING_CALCULATION_VALIDATION = ['Resolved', 'InvoiceReview'];

export const requiresPtsCalculationValidation = (status) =>
    PTS_STATUSES_REQUIRING_CALCULATION_VALIDATION.includes(status);

export const calculateExpectedAmount = (time, rate) => {
    const timeNum = Number(time);
    const rateNum = Number(rate);

    if (!timeNum || !rateNum) {
        return null;
    }

    return Number((timeNum * rateNum + FLOATING_POINT_OFFSET).toFixed(2));
};

export const roundToCurrency = (value) => Number(Number(value).toFixed(2));

const amountsMatch = (expectedAmount, actualAmount) =>
    expectedAmount === roundToCurrency(actualAmount);

const formatRateForCalculation = (rate) => {
    const numericRate = Number(rate);
    if (Number.isNaN(numericRate)) {
        return rate;
    }

    return parseFloat(numericRate.toFixed(4)).toString();
};

const formatTimeForCalculation = (time) => {
    const numericTime = Number(time);
    if (Number.isNaN(numericTime)) {
        return time;
    }

    return Number.isInteger(numericTime)
        ? numericTime.toString()
        : parseFloat(numericTime.toFixed(2)).toString();
};

const getLocationName = (entry, locations = []) => {
    if (entry?.locationName) {
        return entry.locationName;
    }

    const location = locations.find(({ locationId }) => locationId === entry?.locationId);
    return location?.locationName ?? '-';
};

const validateSide = ({ time, rate, amount, side, entry, locations }) => {
    if (!rate || !time || amount == null || amount === '') {
        return null;
    }

    const expectedAmount = calculateExpectedAmount(time, rate);

    if (expectedAmount == null || amountsMatch(expectedAmount, amount)) {
        return null;
    }

    const formattedRate = formatRateForCalculation(rate);
    const formattedTime = formatTimeForCalculation(time);

    return {
        entryId: entry.id,
        transactionDate: formatUtcDate(entry.date, DATE_FORMATS.MD),
        location: getLocationName(entry, locations),
        rateType: entry.rateType ?? '-',
        payTypeId: entry.payTypeId ?? '-',
        side,
        sideLabel: side === 'pay' ? 'Pay Amount' : 'Bill Amount',
        time: formattedTime,
        rate: formattedRate,
        currentAmount: roundToCurrency(amount),
        expectedAmount,
        calculation: `${formattedTime} × ${formattedRate} = ${expectedAmount.toFixed(2)}`,
    };
};

const getPayValidationFields = (entry) => ({
    time: entry.payTime ?? entry.payMiles,
    rate: entry.payRate ?? entry.rate,
    amount: entry.payAmount,
});

const getBillValidationFields = (entry) => ({
    time: entry.billTime ?? entry.billMiles,
    rate: entry.billRate ?? entry.rate,
    amount: entry.billAmount,
});

export const isMileageBillableEntry = (entry) =>
    Boolean(entry?.isMileage) && Boolean(entry?.isBillable);

export const getInvalidMileageBillableField = ({ payUnits, billUnits, payAmount, billAmount }) => {
    if (Number(payUnits) !== Number(billUnits)) {
        return 'billUnits';
    }

    const roundedBill =
        billAmount == null || billAmount === '' ? null : roundToCurrency(billAmount);
    if (roundedBill == null || roundedBill === 0) {
        return 'billAmount';
    }

    const roundedPay = payAmount == null || payAmount === '' ? null : roundToCurrency(payAmount);
    if (roundedPay == null || roundedBill !== roundedPay) {
        return 'billAmount';
    }

    return null;
};

const validateMileageBillableEntry = (entry, locations = []) => {
    if (!isMileageBillableEntry(entry)) {
        return null;
    }

    const invalidField = getInvalidMileageBillableField({
        payUnits: entry.payTime ?? entry.payMiles,
        billUnits: entry.billTime ?? entry.billMiles,
        payAmount: entry.payAmount,
        billAmount: entry.billAmount,
    });
    if (!invalidField) {
        return null;
    }

    return {
        entryId: entry.id,
        transactionDate: formatUtcDate(entry.date, DATE_FORMATS.MD),
        location: getLocationName(entry, locations),
        rateType: entry.rateType ?? '-',
        payTypeId: entry.payTypeId ?? '-',
        side: 'mileageBillable',
        sideLabel: 'Mileage-Billable',
        message: MILEAGE_BILLABLE_VALIDATION_MESSAGE,
    };
};

export const validatePtsDetailCalculation = (entry, locations = []) => {
    const errors = [];

    const payError = validateSide({
        ...getPayValidationFields(entry),
        side: 'pay',
        entry,
        locations,
    });
    if (payError) {
        errors.push(payError);
    }

    const billError = validateSide({
        ...getBillValidationFields(entry),
        side: 'bill',
        entry,
        locations,
    });
    if (billError) {
        errors.push(billError);
    }

    const mileageBillableError = validateMileageBillableEntry(entry, locations);
    if (mileageBillableError) {
        errors.push(mileageBillableError);
    }

    return errors;
};

export const validatePtsDetailCalculations = (entries = [], locations = []) =>
    entries.flatMap((entry) => validatePtsDetailCalculation(entry, locations));

export const formatPtsDetailCalculationError = (error) => {
    const linePrefix = `Line: ${error.transactionDate} | ${error.location} | ${error.rateType} | Pay Type ID ${error.payTypeId}`;

    if (error.message) {
        return [linePrefix, `Issue: ${error.sideLabel}`, error.message].join('\n');
    }

    return [
        linePrefix,
        `Issue: ${error.sideLabel}`,
        `Current Amount: ${currencyFormatter(error.currentAmount)}`,
        `Expected Amount: ${currencyFormatter(error.expectedAmount)}`,
        `Calculation: ${error.calculation}`,
    ].join('\n');
};
